import { Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useModal, usePage, useResource, useStore, useTheme, useToast } from "@/comn/hooks";
import { comnUtils } from "@/comn/utils";
import {
    APIS,
    PGEUIDS,
    SF_MENU_MGMT,
    SG_MENU_LANG_LIST,
    URLS,
} from "@/tz/go/egaz/osci/ptl/ptl/menu/services/OsciMenuService";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const OSCI0104002S = (): JSX.Element => {
    const { pgeUid } = usePage({
        auth: ["auth_osci0003"],
    });
    const toast = useToast();
    const modal = useModal();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const langCodes = ["KO", "EN", "TZ"];
    const { uprMenuId } = useParams();
    const { getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0088" },
            { area: "comnCd", comnCd: "OSC0095" },
        ],
    });

    const form = { menuMgmt: useForm({ defaultSchema: SF_MENU_MGMT }) };

    const grid = { menuLangList: useGrid({ defaultSchema: SG_MENU_LANG_LIST }) };

    const fetch = {
        getChkMenuId: useFetch({
            api: (data: any) => APIS.getChkMenuId(data),
            onSuccess: (data: any) => {
                const { chkMenuId } = data;

                form.menuMgmt.handleSubmit(
                    () => {
                        if (!comnUtils.isEmpty(chkMenuId.content)) {
                            modal.openModal({ content: "msg.osc.00012" }); // Duplicate data exists. !== 중복된 데이터가 존재합니다. ==!
                            form.menuMgmt.setError("menuId", { message: "msg.osc.00012" }); // Duplicate data exists. !== 중복된 데이터가 존재합니다. ==!
                            return;
                        }

                        modal.openModal({
                            content: "msg.osc.00058", // 저장하시겠습니까?
                            onConfirm: () => {
                                grid.menuLangList.clearData();

                                langCodes.forEach((langCd) => {
                                    const menuNm = form.menuMgmt.getValue(`menuNm${langCd}`);
                                    if (!comnUtils.isEmpty(menuNm))
                                        grid.menuLangList.addRow({ langCd: langCd, menuNm: menuNm });
                                });
                                fetch.createMenuMgmt.fetch();
                            },
                        });
                    },
                    () => toast.showToast({ type: "warning", content: "msg.osc.00003" }), // Data is invalid. !== 데이터가 유효하지 않습니다. ==!
                )();
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // 조회가 실패하였습니다.
        }),
        getMenuInfo: useFetch({
            api: (data: any) => APIS.getMenuInfo(data),
            onSuccess: (data: { menuInfo: { content: any } }) => {
                const { menuLangList, ...info } = data.menuInfo.content;
                form.menuMgmt.setValue("bsopClsfCd", info.bsopClsfCd);
                form.menuMgmt.setValue("ptlTpCd", info.ptlTpCd);
                form.menuMgmt.setValue("uprMenuId", info.menuId);
                form.menuMgmt.setValue("uprMenuNm", info.menuNm);
                form.menuMgmt.setValue("menuOrd", 0);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // 조회가 실패하였습니다.
        }),
        createMenuMgmt: useFetch({
            api: () =>
                APIS.createMenuMgmt({
                    ...form.menuMgmt.getValues(),
                    bsopClsfCd: form.menuMgmt.getValue("bsopClsfCd"),
                    ptlTpCd: form.menuMgmt.getValue("ptlTpCd"),
                    menuLangList: grid.menuLangList.getData(),
                }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00073",
                    close: false,
                    onConfirm: () => {
                        handler.onListBtnClick();
                    },
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00045" }), // 저장이 실패하였습니다.
        }),
    };

    const handler = {
        onListBtnClick: () => navigate(`${URLS.osci0104001q}`),
        onMenuMgmtSaveBtnClick: () => {
            const menuId = form.menuMgmt.getValue("menuId");
            const ptlTpCd = form.menuMgmt.getValue("ptlTpCd");
            fetch.getChkMenuId.fetch({ menuId, ptlTpCd });
        },
    };

    useEffect(() => {
        const store = getStore(PGEUIDS.osci0104001q);
        if (!comnUtils.isEmpty(store)) {
            form.menuMgmt.setValue("bsopClsfCd", store.bsopClsfCd);
            form.menuMgmt.setValue("ptlTpCd", store.ptlTpCd);
        }

        form.menuMgmt.setEditable(true);
        form.menuMgmt.setEditable("uprMenuNm", false);
        form.menuMgmt.setEditable("menuId", false);
        // Service Category, Portal Type 은 고정값이므로 수정할 수 없도록 한다.
        form.menuMgmt.setEditable("bsopClsfCd", false);
        form.menuMgmt.setEditable("ptlTpCd", false);

        if (comnUtils.isEmpty(uprMenuId) || uprMenuId === "menu_root") {
            form.menuMgmt.setValue("uprMenuNm", "ROOT");
            form.menuMgmt.setValue("uprMenuId", "menu_root");
            form.menuMgmt.setValue("menuOrd", 0);
        } else {
            fetch.getMenuInfo.fetch(uprMenuId);
        }

        grid.menuLangList.setData({ content: langCodes });
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.bsopClsfCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.ptlTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.menuId} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.uprMenuNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.menuOrd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.menuNmTZ} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.menuNmEN} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.menuMgmt.schema.menuNmKO} />
                        </Group.Row>
                    </Group.Section>

                    <Group.Section hidden={true}>
                        <Grid {...grid.menuLangList.grid} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onMenuMgmtSaveBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
